import { useState, ComponentType, FC } from "react";
import { fetchAuthSession, signInWithRedirect } from "aws-amplify/auth";
import { useMountEffect } from "../hooks";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { closeDialog, openDialog, updateProfile } from "@modules";
import { clog, postApi } from "@libs/utils";
import {
  PARTNER_WITH_SSO,
  Permission,
  PostPermissionResponse,
  PostPermissionsRequest,
  ResponseStatus,
} from "@types";
import { API_FE_CONFIG, awsConfig } from "@environment";

const withProtection = <P,>(
  Component: FC<P>,
  pageName: string = "",
): ComponentType<any> => {
  const MutatedComponent: FC<P> = (props: any) => {
    const navigate = useNavigate();
    const [isAuthorized, SetIsAuthorized] = useState(false);
    const [isGranted, SetIsGranted] = useState(false);
    const dispatch = useDispatch();

    useMountEffect(async () => {
      let partner = "";
      try {
        const session = await fetchAuthSession();
        const payload = session.tokens?.idToken?.payload as any;
        partner = payload["custom:partner"] || "";

        if (partner.length === 0) {
          throw new Error("Unauthorized");
        }

        const respose = await postApi<
          PostPermissionsRequest,
          PostPermissionResponse
        >(API_FE_CONFIG.POST_AUTH_PERMISSIONS, { empty: true });

        let permissions: Permission[] = [];

        if (respose.status === ResponseStatus.SUCCESS && respose.data) {
          permissions = respose.data.permissions || [];
        } else {
          dispatch(
            openDialog({
              title: "Error",
              description: "Failed to fetch user permissions.",
              primaryButtonLabel: "Sign Out",
              onClickPrimaryButton: async () => {
                dispatch(closeDialog());
                navigate("/logout");
              },
            }),
          );
        }

        dispatch(
          updateProfile({
            ...payload,
            permissions: permissions,
            username: payload["cognito:username"] || "",
            groups: payload["cognito:groups"] || [],
            partner: partner,
          }),
        );
        SetIsAuthorized(true);
        const checkedPermissions = permissions.find(
          (permission) =>
            permission.appCode === pageName ||
            permission.menus?.some((menu) => menu.menuCode === pageName),
        );

        if (PARTNER_WITH_SSO.includes(partner)) {
          if (checkedPermissions !== undefined || pageName === "APP_PORTAL") {
            SetIsGranted(true);
            return;
          }

          throw new Error("Unauthorized");
        }

        if (
          !PARTNER_WITH_SSO.includes(partner) &&
          window.location.pathname.includes(partner.toLocaleLowerCase())
        ) {
          if (checkedPermissions !== undefined || pageName === "APP_PORTAL") {
            SetIsGranted(true);
            return;
          }

          throw new Error("Unauthorized");
        }

        if (
          !PARTNER_WITH_SSO.includes(partner) &&
          window.location.pathname.includes(partner.toLocaleLowerCase())
        ) {
          window.open(
            `/${partner.toLocaleLowerCase()}${window.location.pathname}`,
            "_self",
          );

          return;
        }

        throw new Error("Unauthorized");
      } catch (e) {
        const username = localStorage.getItem(
          `CognitoIdentityServiceProvider.${awsConfig.Auth.Cognito.userPoolClientId}.LastAuthUser`,
        );
        const partnerFromStorage = localStorage.getItem(
          `PartnerPortalServiceProvider.${awsConfig.Auth.Cognito.userPoolClientId}.${username}.partner`,
        );

        if (partnerFromStorage) {
          navigate(`/${partnerFromStorage.toLocaleLowerCase()}`);
          return;
        }

        const urlPath = window.location.pathname;
        const pathSegments = urlPath.split("/");
        const partnerPath = pathSegments.length > 1 ? pathSegments[0] : "";
        clog("partnerPath", partnerPath);

        if (partnerPath !== "" && !PARTNER_WITH_SSO.includes(partnerPath)) {
          navigate(`/${partner ? partner.toLocaleLowerCase() : partnerPath}`); // Redirect to partner-specific login
          return;
        }

        // navigate("/login"); // Redirect to general login
        signInWithRedirect();
      }
    });

    return isGranted && isAuthorized ? (
      <>
        <Component {...props} />
      </>
    ) : null;
  };

  return MutatedComponent;
};

export default withProtection;
